import _ from "@/boot/lodash";
import i18n from "@/i18n";
import router from "@/router";
import store, { IState } from "@/store";
import { ActionTree, MutationTree, GetterTree } from "vuex";
import { IUpmindModule } from "@/models/upmindModules";
import { Methods } from "@/models/methods";
import { UpmindModuleCodes } from "@/data/enums/upmindModules";
import { ApiPathGetter } from "@/models/api";

export interface IOrgState {
  enabledModules: IUpmindModule[];
  injectedModules: UpmindModuleCodes[];
}

const initialState = (): IOrgState => {
  return {
    enabledModules: [],
    injectedModules: []
  };
};

const getters: GetterTree<IOrgState, IState> = {
  apiPath: (s, g, rS, { isAdminContext }): ApiPathGetter => () => {
    const admin = `api/admin/org/modules`;
    const client = `api/org/modules`;
    const contextual = isAdminContext ? admin : client;
    return { client, admin, contextual };
  },
  availableModules: state => state.enabledModules.map(module => module.code),
  canRegisterModule: (state, getters) => (code: UpmindModuleCodes) => {
    const isAvailable = getters.availableModules.includes(code);
    const isInjected = state.injectedModules.includes(code);
    return isAvailable && !isInjected;
  }
};

const actions: ActionTree<IOrgState, IState> = {
  getEnabledModules: async ({ commit, dispatch, getters }) => {
    try {
      const { data } = await dispatch(
        "api/call",
        {
          method: Methods.GET,
          path: getters.apiPath().contextual,
          callConfig: {
            withAccessToken: false
          }
        },
        { root: true }
      );
      commit("enabledModules", data);
    } catch (e) {
      // Silent (non-blocking) catch
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },
  getAndRegisterModules: async ({ getters, dispatch }) => {
    await dispatch("getEnabledModules");
    for (const code of getters.availableModules as UpmindModuleCodes[]) {
      await dispatch("registerModule", code);
    }
  },
  registerModule: async (
    { getters, rootState, commit },
    code: UpmindModuleCodes
  ) => {
    if (!getters.canRegisterModule(code)) return;
    try {
      // Import routes
      const routes = (await import(`@/modules/${code}/router`)).default;
      // Add imported routes
      _.forEach(routes, route => router.addRoute("adminRoute", route));
      // Register store module
      store.registerModule(
        `data/${code}`,
        (await import(`@/modules/${code}/store`)).default
      );
      // Load language packs
      await store.dispatch("i18n/loadLanguagePacks", {
        localeCode: rootState.i18n.activeLocaleCode,
        packs: [`_modules.${code}`]
      });
      // Commit injection
      commit("injectModule", code);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(i18n.t("_error.module_not_found", { module: code }), error);
    }
  }
};

const mutations: MutationTree<IOrgState> = {
  enabledModules: (state, modules) => {
    state.enabledModules = _.uniqBy(
      [...modules, ...state.enabledModules],
      i => i.code
    );
  },
  injectModule: ({ injectedModules }, code: UpmindModuleCodes) => {
    if (!injectedModules?.includes(code)) injectedModules.push(code);
  }
};

export default {
  namespaced: true,
  state: initialState(),
  getters,
  actions,
  mutations
};
